import { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, styled, Typography } from '@mui/material';
import { ReactComponent as CoinDark } from '@/assets/images/coin-dark.svg';
import { RewardType } from '@/constants/rewards';
import RewardRow from './RewardRow';

const Container = styled('main')(() => ({
  flex: 1,
  gap: 24,
  display: 'flex',
  flexDirection: 'column',
}));

const SubHeader = styled(Box)(({ theme }) => ({
  padding: 16,
  gap: 2,
  marginTop: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  borderBottomColor: theme.palette.border.main,
}));

const RewardsContainer = styled(Box)(() => ({
  flex: 1,
  paddingInline: 16,
  maxWidth: 600,
  margin: 'auto',

  '@supports (width: -webkit-fill-available)': {
    width: '-webkit-fill-available',
  },

  '@supports (width: -moz-available)': {
    width: '-moz-available',
  },
}));

const Points = styled(Box)(() => ({
  paddingInline: 8,
  paddingBlock: 6,
  borderRadius: 8,
  gap: 4,
  backgroundColor: '#000000',
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
}));

type Coin = {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  delay: number;
};

type Props = {
  points: number;
  title: string;
  rewards: RewardType[];
  onCollet: (reward: RewardType) => void;
};

const COINS_NO = 10;
const ANIMATION_DURATION = 1200;
const COIN_DELAY = 0.1;

const getRandomValue = (min = 100, max = 1000) => Math.floor(Math.random() * (max - min + 1)) + min;

export default function RewardsForm({ points, title, rewards, onCollet }: Props) {
  const [coins, setCoins] = useState<Coin[]>([]);

  const animateCoins = () => {
    const newCoins = Array.from({ length: COINS_NO }, (_, i) => ({
      id: i,
      startX: 0,
      startY: 0,
      endX: getRandomValue(-200, 100),
      endY: getRandomValue(100, 800),
      delay: COIN_DELAY,
    }));

    setCoins(newCoins);

    const timeout = setTimeout(() => setCoins([]), ANIMATION_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  };

  return (
    <Container>
      <SubHeader>
        <Box
          display="flex"
          justifyContent="space-between"
          margin="auto"
          width="100%"
          maxWidth={600}
        >
          <Typography fontSize={28} fontWeight={700} lineHeight="32px" color="black">
            Rewards
          </Typography>
          <Points>
            <Box sx={{ position: 'absolute', top: 6, left: 16 }}>
              {coins.map((coin) => (
                <motion.div
                  key={coin.id}
                  style={{ zIndex: 1000, position: 'absolute' }}
                  initial={{ x: coin.startX, y: coin.startY, opacity: 1 }}
                  animate={{
                    x: coin.endX,
                    y: coin.endY,
                    opacity: 0,
                    transition: { duration: 1, delay: coin.delay, ease: 'easeIn' },
                  }}
                >
                  <CoinDark />
                </motion.div>
              ))}
            </Box>
            <CoinDark />
            <Typography fontSize={14} fontWeight={600} lineHeight="16px" color="white">
              {points}
            </Typography>
          </Points>
        </Box>
      </SubHeader>
      <RewardsContainer>
        <Typography marginBottom={1} fontSize={20} fontWeight={700} lineHeight="24px" color="black">
          {title}
        </Typography>
        {rewards.map((item, index) => (
          <RewardRow
            {...item}
            key={item.id}
            index={index}
            onClick={() => {
              onCollet(item);
              animateCoins();
            }}
          />
        ))}
      </RewardsContainer>
    </Container>
  );
}
